import React, { useEffect } from 'react';
import clsx from 'clsx';
import { is, head } from 'ramda';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button } from '@material-ui/core';
import { calculateQuizValues } from 'utils/quizHelper';
import QuizQuestionPresenter from 'presenters/QuizQuestionPresenter';
import QuizPresenter from 'presenters/QuizPresenter';
import InputWithSlider from 'sharedComponents/InputWithSlider';

import useStyles from './useStyles';

const MONTHS_CUSHION_DEFAULT_VALUES = {
  suggested: 2,
  stretched: 1.5,
  aggressive: 1,
};

const MONTHS_IN_YEAR = 12;

const SavingsMinimumQuestion = props => {
  const { t } = useTranslation();
  const { question, onChange, values, setValidity } = props;
  const classes = useStyles();

  const monthCusionField = head(QuizQuestionPresenter.quizFields(question));
  const currentValue = QuizPresenter[monthCusionField](values);

  useEffect(() => {
    const isValid = is(Number, currentValue);
    setValidity(isValid);
  }, [currentValue]);

  const options = QuizQuestionPresenter.options(question);
  const { min, max, marksDivider, strictLimit } = options;
  const { savingsCushion, incomeValue } = calculateQuizValues(values);
  const minSavingsCusion = (incomeValue / MONTHS_IN_YEAR) * Number(min);
  const maxSavingsCusion = (incomeValue / MONTHS_IN_YEAR) * Number(max);

  const handleValueChange = value => {
    const months = (value * MONTHS_IN_YEAR) / incomeValue;
    onChange({ field: monthCusionField, value: months });
  };

  const handleClickMonthsCusionButton = type => () => {
    onChange({ field: monthCusionField, value: MONTHS_CUSHION_DEFAULT_VALUES[type] });
  };

  const isSuggested = currentValue >= MONTHS_CUSHION_DEFAULT_VALUES.suggested;
  const isStretched =
    currentValue < MONTHS_CUSHION_DEFAULT_VALUES.suggested && currentValue >= MONTHS_CUSHION_DEFAULT_VALUES.stretched;
  const isAggressive = currentValue < MONTHS_CUSHION_DEFAULT_VALUES.stretched;

  return (
    <Box>
      <Typography variant="h1">{QuizQuestionPresenter.label(question)}</Typography>
      <Box className={classes.controlBar}>
        <Button
          key="aggressive"
          onClick={handleClickMonthsCusionButton('aggressive')}
          className={clsx(classes.button, { [classes.aggressive]: isAggressive }, classes.hoverAggressive)}
        >
          {t('components.Quiz.SavingsMinimumQuestion.aggressive')}
        </Button>
        <Button
          key="stretched"
          onClick={handleClickMonthsCusionButton('stretched')}
          className={clsx(classes.button, { [classes.stretched]: isStretched }, classes.hoverStretched)}
        >
          {t('components.Quiz.SavingsMinimumQuestion.stretched')}
        </Button>
        <Button
          key="suggested"
          onClick={handleClickMonthsCusionButton('suggested')}
          className={clsx(classes.button, { [classes.suggested]: isSuggested }, classes.hoverSuggested)}
        >
          {t('components.Quiz.SavingsMinimumQuestion.suggested')}
        </Button>
      </Box>
      <Box>
        <InputWithSlider
          className={classes.input}
          value={savingsCushion}
          min={minSavingsCusion}
          max={maxSavingsCusion}
          marksDivider={marksDivider}
          label={QuizQuestionPresenter.inputLabel(question)}
          onChange={handleValueChange}
          strictLimit={strictLimit}
        />
      </Box>
      <Box className={classes.calculationsBar}>
        <Box className={classes.calculationsItem}>
          <Typography variant="body1" color="secondary">
            {t('components.Quiz.SavingsMinimumQuestion.savings')}
          </Typography>
          <Typography variant="h1">{QuizPresenter.formattedMoney(savingsCushion)}</Typography>
        </Box>
        <Box className={classes.calculationsItem}>
          <Typography variant="body1" color="secondary">
            {t('components.Quiz.SavingsMinimumQuestion.months')}
          </Typography>
          <Typography variant="h1">{currentValue.toFixed(1)}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

SavingsMinimumQuestion.propTypes = {
  question: QuizQuestionPresenter.shape().isRequired,
  onChange: PropTypes.func.isRequired,
  setValidity: PropTypes.func.isRequired,
  values: QuizPresenter.shape().isRequired,
};

export default SavingsMinimumQuestion;



// WEBPACK FOOTER //
// ./src/sharedComponents/Quiz/components/SavingsMinimumQuestion/SavingsMinimumQuestion.js